import { useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

import { Symbol, MenuDisplayProps } from './types';

import {
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  useTheme,
  capitalize,
} from '@mui/material';
import { radiusQuarter } from 'utility/borderRadius';

export const MenuDisplay = forwardRef((props: MenuDisplayProps, ref) => {
  const theme = useTheme();
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedSymbolIndex, setSelectedSymbolIndex] = useState(0);

  const selectItem = useCallback(
    (groupIndex: number, symbolIndex: number) => {
      const symbol = {
        ...props.items[groupIndex].symbols[symbolIndex],
        action: (editor: any) => {
          editor.chain().focus().insertContent(symbol.icon).run();
        },
      };
      props.command(symbol);
    },
    [props],
  );

  const createCommandClickHandler = useCallback(
    (groupIndex: number, symbolIndex: number) => {
      return () => {
        selectItem(groupIndex, symbolIndex);
      };
    },
    [selectItem],
  );

  const navigateMenu = (direction: 'up' | 'down') => {
    if (!props.items.length) {
      return false;
    }

    let newSymbolIndex: number = selectedSymbolIndex || 0;
    let newGroupIndex: number = selectedGroupIndex || 0;

    if (direction === 'down') {
      newSymbolIndex += 1;

      if (props.items[selectedGroupIndex]?.symbols.length - 1 < newSymbolIndex) {
        newSymbolIndex = 0;
        newGroupIndex += 1;
      }

      if (props.items.length - 1 < newGroupIndex) {
        newGroupIndex = 0;
      }
    } else if (direction === 'up') {
      newSymbolIndex -= 1;

      if (newSymbolIndex < 0) {
        newGroupIndex -= 1;
        newSymbolIndex = props.items[newGroupIndex]?.symbols.length - 1 || 0;
      }

      if (newGroupIndex < 0) {
        newGroupIndex = props.items.length - 1;
        newSymbolIndex = props.items[newGroupIndex].symbols.length - 1;
      }
    }

    setSelectedSymbolIndex(newSymbolIndex);
    setSelectedGroupIndex(newGroupIndex);

    return true;
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === 'ArrowDown') {
        return navigateMenu('down');
      }

      if (event.key === 'ArrowUp') {
        return navigateMenu('up');
      }

      if (event.key === 'Enter') {
        selectItem(selectedGroupIndex, selectedSymbolIndex);
      }
    },
  }));

  // Anytime the groups change, i.e. the user types to narrow it down, we want to
  // reset the current selection to the first menu item
  useEffect(() => {
    setSelectedGroupIndex(0);
    setSelectedSymbolIndex(0);
  }, [props.items]);

  if (!props.items.length) {
    return null;
  }

  return (
    <MenuList
      aria-label="Editorial Content Item Menu Actions"
      className="fab_content_menu_actions"
      key="colon-command-menu-list"
      sx={(theme) => ({
        padding: 0,
        overflowY: 'auto',
        maxHeight: 300,
        width: 250,
        borderRadius: radiusQuarter(theme),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        '& .MuiListItem-root': {
          px: 1 / 2,
          py: 1 / 4,
        },
        '& .MuiListItemIcon-root': {
          minWidth: '0 !important',
        },
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.grey[800]
              : theme.palette.grey[300],
          borderRadius: radiusQuarter(theme),
        },
        '& .MuiSvgIcon-root , & .MuiTypography-root': {
          fontSize: '1rem',
        },
        '& .MuiButtonBase-root': {
          gap: 1,
          '&.Mui-selected': {
            backgroundColor: theme.palette.action.selected,
          },
        },
      })}
    >
      {props.items.map((group, groupIndex: number) => (
        <div key={groupIndex}>
          <ListSubheader
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontSize: '1.125rem',
              lineHeight: '2.5rem',
            }}
          >
            {group.name}
          </ListSubheader>
          {group.symbols.map((symbol: Symbol, symbolIndex: number) => (
            <MenuItem
              key={`${groupIndex}-${symbolIndex}`}
              selected={
                selectedGroupIndex === groupIndex && selectedSymbolIndex === symbolIndex
              }
              onClick={createCommandClickHandler(groupIndex, symbolIndex)}
            >
              <ListItemIcon>{symbol.icon}</ListItemIcon>
              <ListItemText
                primary={capitalize(symbol.label)}
                sx={{ color: theme.palette.text.primary }}
              />
            </MenuItem>
          ))}
        </div>
      ))}
    </MenuList>
  );
});

export default MenuDisplay;
